import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/App";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Check, X, DollarSign, User, Mail, CreditCard, Receipt, Settings as SettingsIcon, Upload, Loader2, Download, AlertTriangle, CheckCircle, ChevronDown, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, getCurrencySymbol } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { useCheckout } from "@/hooks/useCheckout";
import { CALENDAR_LABELS, type HolidayCalendar } from "@/lib/holidays";
import { detectBillingCurrency, detectBillingCurrencyAsync } from "@/components/SubscriptionModal";

export default function SettingsPage() {
  const { isDark, toggleTheme, currency: themeCurrency, setCurrencyDirect, dateFormat, setDateFormat, holidayCalendar, setHolidayCalendar } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { startCheckout, openPortal, isLoading: checkoutLoading, error: checkoutError } = useCheckout();

  const PLAN_PRICES: Record<string, { sym: string; lead: string; junior: string }> = {
    USD: { sym: '$',  lead: '9.50', junior: '6.50' },
    GBP: { sym: '£',  lead: '9.50', junior: '6.50' },
    EUR: { sym: '€',  lead: '9.50', junior: '6.50' },
  };

  // Profile state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // Email change state
  const [showEmailChange, setShowEmailChange]   = useState(false);
  const [newEmail, setNewEmail]                 = useState("");
  const [emailChanging, setEmailChanging]       = useState(false);
  const [emailChangeMsg, setEmailChangeMsg]     = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rates state
  const [dayRate, setDayRate] = useState<number | string>(700);
  const [hourlyRate, setHourlyRate] = useState<number | string>(87.5);

  // Currency state
  const [currencyCode, setCurrencyCode] = useState<string>(themeCurrency || "USD");
  const [billingCurrency, setBillingCurrency] = useState(detectBillingCurrency());
  const pricing = PLAN_PRICES[billingCurrency] ?? PLAN_PRICES['USD'];

  useEffect(() => {
    detectBillingCurrencyAsync().then(c => setBillingCurrency(c));
  }, []);

  // Business / Invoice presets
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [invoicePrefix, setInvoicePrefix] = useState("INV-");
  const [defaultTaxRate, setDefaultTaxRate] = useState<number | string>(0);
  const [defaultNotes, setDefaultNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("NET30");

  // Subscription state
  const subscription = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<'lead' | 'junior'>('lead');

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Collapsible sections — defaults: profile+rates+subscription open; invoicing+export+deletion collapsed
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('fh_settings_collapsed');
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return { profile: false, rates: false, invoicing: true, export: true, subscription: false, deletion: true };
  });

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('fh_settings_collapsed', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Tax export state
  const [exportYear, setExportYear]   = useState<string>(String(new Date().getFullYear()));
  const [exporting, setExporting]     = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportYears, setExportYears] = useState<string[]>([]);

  // GDPR state
  const [region, setRegion]               = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting]           = useState(false);

  // Tracks whether initial data has loaded — prevents auto-save from firing on mount
  const hasLoaded = useRef(false);

  // Load from Supabase on mount
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const [{ data: userData }, { data: settingsData }] = await Promise.all([
        supabase
          .from('users')
          .select('first_name, last_name, email, default_rate, default_rate_type, default_currency')
          .eq('id', session.user.id)
          .maybeSingle(),
        supabase
          .from('app_settings')
          .select('business_profile, region')
          .eq('user_id', session.user.id)
          .maybeSingle(),
      ]);

      if (userData) {
        if (userData.first_name) setFirstName(userData.first_name);
        if (userData.last_name) setLastName(userData.last_name);
        setEmail(userData.email || session.user.email || "");
        if (userData.default_rate_type === 'hourly') {
          if (userData.default_rate) setHourlyRate(userData.default_rate);
        } else {
          if (userData.default_rate) setDayRate(userData.default_rate);
        }
        if (userData.default_currency) setCurrencyCode(userData.default_currency);
        else setCurrencyCode('USD');
      } else {
        setEmail(session.user.email || "");
      }

      if (settingsData?.business_profile) {
        const bp = settingsData.business_profile;
        if (bp.name !== undefined) setBusinessName(bp.name);
        if (bp.address !== undefined) setBusinessAddress(bp.address);
        if (bp.regNumber !== undefined) setRegNumber(bp.regNumber);
        if (bp.bankName !== undefined) setBankName(bp.bankName);
        if (bp.accountNumber !== undefined) setAccountNumber(bp.accountNumber);
        if (bp.routingNumber !== undefined) setRoutingNumber(bp.routingNumber);
        if (bp.invoicePrefix !== undefined) setInvoicePrefix(bp.invoicePrefix);
        if (bp.defaultTaxRate !== undefined) setDefaultTaxRate(bp.defaultTaxRate);
        if (bp.defaultNotes !== undefined) setDefaultNotes(bp.defaultNotes);
        if (bp.paymentTerms !== undefined) setPaymentTerms(bp.paymentTerms);
      }

      // Region: load from DB; if not set, detect from browser timezone and write
      const detectedRegion = (() => {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          return tz.startsWith('Europe/') ? 'EU' : null;
        } catch { return null; }
      })();
      const effectiveRegion = detectedRegion; // always use current timezone
      setRegion(effectiveRegion);
      if (effectiveRegion) {
        // Keep DB in sync with current timezone
        supabase.from('app_settings').upsert(
          { user_id: session.user.id, region: effectiveRegion },
          { onConflict: 'user_id' }
        ).then(({ error }) => {
          if (error) console.error('[settings] region save failed:', error);
        });
      }

      // Fetch years for export year selector
      const [{ data: bookingYears }, { data: yearlyRows }] = await Promise.all([
        supabase.from('bookings').select('start_date').eq('user_id', session.user.id).eq('archived', false),
        supabase.from('yearly_performance').select('year').eq('user_id', session.user.id).eq('archived', false),
      ]);
      const yearsSet = new Set<string>();
      bookingYears?.forEach(b => { if (b.start_date) yearsSet.add(String(new Date(b.start_date).getFullYear())); });
      yearlyRows?.forEach(y => { if (y.year) yearsSet.add(String(y.year)); });
      setExportYears(Array.from(yearsSet).sort((a, b) => Number(b) - Number(a)));

      // Defer enabling auto-save until AFTER React has processed the batched
      // state updates above — prevents a spurious save on initial data load
      setTimeout(() => { hasLoaded.current = true; }, 0);
    })();
  }, []);

  // ── Auto-save: debounced 900ms after any field change ─────────────────────
  useEffect(() => {
    if (!hasLoaded.current) return;
    setSaveStatus('saving');
    const timer = setTimeout(() => { doSave(false); }, 900);
    return () => clearTimeout(timer);
  }, [firstName, lastName, dayRate, hourlyRate, currencyCode,
      businessName, businessAddress, regNumber, bankName,
      accountNumber, routingNumber, invoicePrefix, defaultTaxRate,
      defaultNotes, paymentTerms]);

  // ── Deep link hash scroll ─────────────────────────────────────────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const hashToKey: Record<string, string> = {
      '#profile-section': 'profile',
      '#export-section': 'export',
      '#subscription-section': 'subscription',
    };
    const key = hashToKey[hash];
    if (key) {
      setCollapsedSections(prev => ({ ...prev, [key]: false }));
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, []);

  // ── Core save logic (shared by auto-save and manual button) ───────────────
  const doSave = useCallback(async (showToast: boolean) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      if (showToast) toast({ title: "Not signed in", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const [usersResult, settingsResult] = await Promise.all([
        supabase.from('users').upsert(
          { id: session.user.id,
            email: email || session.user.email || '',
            first_name: firstName, last_name: lastName,
            default_rate: Number(dayRate) || 0, default_rate_type: 'daily', default_currency: currencyCode },
          { onConflict: 'id' }
        ),
        // TODO pre-launch: migrate regNumber to invoice_settings.tax_id with pgcrypto encryption
        supabase.from('app_settings').upsert(
          { user_id: session.user.id, currency: currencyCode,
            business_profile: { name: businessName, address: businessAddress, regNumber,
              bankName, accountNumber, routingNumber, invoicePrefix, defaultTaxRate: Number(defaultTaxRate) || 0, defaultNotes, paymentTerms } },
          { onConflict: 'user_id' }
        ),
      ]);

      if (usersResult.error) throw usersResult.error;
      if (settingsResult.error) throw settingsResult.error;

      setCurrencyDirect(currencyCode);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
      if (showToast) toast({ title: "Settings saved!" });
    } catch (err: any) {
      console.error("[settings save]", err);
      setSaveStatus('idle');
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, dayRate, hourlyRate, currencyCode,
      businessName, businessAddress, regNumber, bankName,
      accountNumber, routingNumber, invoicePrefix, defaultTaxRate,
      defaultNotes, paymentTerms]);

  // handleSave kept for any direct button clicks (delegates to doSave with toast)
  const handleSave = () => doSave(true);

  // ── Tax export ─────────────────────────────────────────────────────────────
  const handleTaxExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      // Read expenses from localStorage and include in export
      let expenses: { description: string; amount: number; freq: string }[] = [];
      try {
        const stored = localStorage.getItem(`fh_expenses_v1_${session.user.id}`);
        if (stored) expenses = JSON.parse(stored);
      } catch { /* ignore */ }

      const res = await fetch('/api/generate-tax-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ year: exportYear, expenses }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Export failed (${res.status})`);
      }
      const { url, filename } = await res.json();
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); }, 1000);
    } catch (err: any) {
      console.error('[settings tax-export]', err);
      setExportError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── GDPR account deletion ─────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE' || !deletePassword) return;
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const response = await fetch('/api/delete-account', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: deletePassword }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Deletion failed (${response.status})`);
      }

      await supabase.auth.signOut();
      window.location.href = '/login';
    } catch (err: any) {
      console.error('[settings delete-account]', err);
      toast({ title: 'Deletion failed', description: err.message, variant: 'destructive' });
      setDeleting(false);
    }
  };

  const currencySymbol = (code: string) => getCurrencySymbol(code);
  const symPadding = (sym: string) => sym.length <= 1 ? 'pl-9' : sym.length === 2 ? 'pl-12' : sym.length === 3 ? 'pl-16' : 'pl-20';

  const pageClasses = isDark
    ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white"
    : "bg-[#E3E8F1] text-[#1A1F36]";

  const cardClasses = cn(
    "backdrop-blur-2xl rounded-[2.5rem] border shadow-xl shadow-blue-900/5",
    isDark
      ? "bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]"
      : "bg-white/80 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white"
  );
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const inputClasses = cn(
    "w-full px-4 py-3 rounded-2xl border transition-all outline-none",
    isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] placeholder-[#1A1F36]/30 focus:border-gray-400"
  );

  const labelClasses = cn(
    "text-sm font-bold uppercase tracking-[0.2em] mb-2 block",
    isDark ? "text-white/40" : "text-[#1A1F36]/40"
  );

  const sectionLabelClasses = cn(
    "text-[10px] font-bold uppercase tracking-[0.2em] mb-4 block",
    isDark ? "text-white/30" : "text-[#1A1F36]/30"
  );

  return (
    <div className={`min-h-screen ${pageClasses} font-sans selection:bg-blue-200 relative overflow-hidden`}>
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} />
          <div className={`absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} style={{ animationDelay: "1s" }} />
        </div>
      )}

      {/* ── Floating Save Status — always visible, auto-saves on edit ── */}
      <AnimatePresence>
        {saveStatus !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            className="fixed bottom-8 right-8 z-50"
          >
            <div className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs font-bold shadow-2xl ${
              saveStatus === 'saved'
                ? (isDark ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border border-emerald-200")
                : (isDark ? "bg-white/10 text-white/60 border border-white/10" : "bg-white text-[#1A1F36]/50 border border-gray-200")
            }`}>
              {saveStatus === 'saving' ? (
                <><Loader2 size={12} className="animate-spin" /> Saving…</>
              ) : (
                <><Check size={12} /> Saved</>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="max-w-[1400px] mx-auto space-y-8 relative z-10 p-4 md:p-8 pt-4 pb-28 md:pb-8">
        <header className="flex justify-between items-start gap-4 md:gap-8 mb-8">
          <div className="flex-1">
            <h1 className="text-4xl md:text-7xl lg:text-8xl font-black tracking-tight mb-2">Settings</h1>
            <p className={cn("ml-3 font-light text-xl", isDark ? "text-white/50" : "text-[#1A1F36]/50")}>
              Manage your account and preferences.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className={cn("w-10 h-10 rounded-lg flex items-center justify-center transition-all", isDark ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-white/60 text-[#1A1F36] hover:bg-white border border-white/60 shadow-sm")}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </motion.button>
          </div>
        </header>

        <div className="flex flex-col gap-8 max-w-[860px]">

          {/* Data Import Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={cn(cardClasses, "p-5 md:p-10")}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                <Upload size={20} />
              </div>
              <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Data Import</h2>
            </div>
            <p className={cn("text-sm font-light leading-relaxed mb-8", isDark ? "text-white/40" : "text-[#1A1F36]/50")}>
              Bring your historical booking and earnings data into FirstHold. Upload a PDF, CSV, XLSX, or TXT to enrich your account with past work — rates, clients, and performance history.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={() => navigate('/import')}
                className={cn(
                  "px-8 py-3.5 rounded-full flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]",
                  isDark
                    ? "bg-white text-[#1A1F36] shadow-lg shadow-white/10 hover:bg-white/90"
                    : "bg-[#1A1F36] text-white shadow-lg shadow-black/15 hover:bg-black"
                )}
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em]">Import Historical Data</span>
                <Download size={14} />
              </button>
              <button
                onClick={() => navigate('/import?step=past_earnings')}
                className={cn(
                  "px-8 py-3.5 rounded-full flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] border",
                  isDark
                    ? "border-white/20 text-white/70 hover:bg-white/10"
                    : "border-[#1A1F36]/20 text-[#1A1F36]/70 hover:bg-black/5"
                )}
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em]">Edit Historical Data</span>
              </button>
            </div>
            <p className={cn("text-[10px] font-bold uppercase tracking-[0.15em] text-center mt-4", isDark ? "text-white/20" : "text-[#1A1F36]/30")}>
              Supports PDF, CSV, XLSX, TXT — booking records, yearly summaries, invoice exports
            </p>
          </motion.div>

          {/* Profile & Rates */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={cn(cardClasses, "p-5 md:p-10")}
          >
            <div className="space-y-12">
              {/* Profile Section */}
              <section id="profile-section">
                <div
                  onClick={() => toggleSection('profile')}
                  className={cn('flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none', collapsedSections['profile'] ? 'mb-0' : 'mb-8', isDark ? 'hover:bg-white/5' : 'hover:bg-black/5')}
                >
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                    <User size={20} />
                  </div>
                  <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Profile</h2>
                  <div className="ml-auto flex items-center gap-2">
                    <span className={cn("text-[10px] font-bold uppercase tracking-widest opacity-40", isDark ? "text-white" : "text-[#1A1F36]")}>
                      {collapsedSections['profile'] ? 'Show' : 'Hide'}
                    </span>
                    <ChevronDown size={16} className={cn("transition-transform duration-200", isDark ? "text-white" : "text-[#1A1F36]")} style={{ transform: collapsedSections['profile'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {!collapsedSections['profile'] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={labelClasses}>First Name</label>
                            <input
                              type="text"
                              className={inputClasses}
                              placeholder="First name"
                              value={firstName}
                              onChange={e => setFirstName(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={labelClasses}>Last Name</label>
                            <input
                              type="text"
                              className={inputClasses}
                              placeholder="Last name"
                              value={lastName}
                              onChange={e => setLastName(e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelClasses}>Email Address</label>
                          <input
                            type="email"
                            className={cn(inputClasses, "opacity-60 cursor-default")}
                            value={email}
                            readOnly
                          />
                        </div>
                        <button
                          onClick={() => setShowPasswordModal(true)}
                          className={`w-full py-3 rounded-2xl border font-bold text-xs uppercase tracking-[0.2em] transition-all ${isDark ? "border-white/10 text-white/40 hover:text-white hover:border-white/30" : "border-gray-200 text-[#1A1F36]/40 hover:text-[#1A1F36] hover:border-gray-400"}`}
                        >
                          Change Password
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>

              {/* Default Rates Section */}
              <section>
                <div
                  onClick={() => toggleSection('rates')}
                  className={cn('flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none', collapsedSections['rates'] ? 'mb-0' : 'mb-8', isDark ? 'hover:bg-white/5' : 'hover:bg-black/5')}
                >
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                    <DollarSign size={20} />
                  </div>
                  <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Default Rates</h2>
                  <div className="ml-auto flex items-center gap-2">
                    <span className={cn("text-[10px] font-bold uppercase tracking-widest opacity-40", isDark ? "text-white" : "text-[#1A1F36]")}>
                      {collapsedSections['rates'] ? 'Show' : 'Hide'}
                    </span>
                    <ChevronDown size={16} className={cn("transition-transform duration-200", isDark ? "text-white" : "text-[#1A1F36]")} style={{ transform: collapsedSections['rates'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {!collapsedSections['rates'] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={labelClasses}>Day Rate</label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">{currencySymbol(currencyCode)}</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                className={cn(inputClasses, symPadding(currencySymbol(currencyCode)))}
                                value={dayRate}
                                onChange={e => setDayRate(e.target.value.replace(/[^0-9.]/g, ''))}
                              />
                            </div>
                          </div>
                          <div>
                            <label className={labelClasses}>Hourly Rate</label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">{currencySymbol(currencyCode)}</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                className={cn(inputClasses, symPadding(currencySymbol(currencyCode)))}
                                value={hourlyRate}
                                onChange={e => setHourlyRate(e.target.value.replace(/[^0-9.]/g, ''))}
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className={labelClasses}>Preferred Currency</label>
                          <div className="flex flex-col gap-2 max-w-[320px]">
                            {/* Quick picks */}
                            <div className={cn("flex p-1 rounded-2xl border w-full", isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200")}>
                              {(["USD", "GBP", "EUR"] as const).map((cur) => (
                                <button
                                  key={cur}
                                  onClick={() => setCurrencyCode(cur)}
                                  className={cn(
                                    "flex-1 py-2 text-xs font-bold rounded-xl transition-all",
                                    currencyCode === cur
                                      ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm")
                                      : "text-gray-400 hover:text-gray-500"
                                  )}
                                >
                                  {currencySymbol(cur)}
                                </button>
                              ))}
                            </div>
                            {/* Other currencies dropdown */}
                            <div className="relative">
                              <select
                                value={["USD","GBP","EUR"].includes(currencyCode) ? "" : currencyCode}
                                onChange={e => { if (e.target.value) setCurrencyCode(e.target.value); }}
                                className={cn(
                                  "w-full pl-3 pr-8 py-2 rounded-2xl border text-xs font-bold appearance-none transition-all outline-none",
                                  isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-100 border-gray-200 text-black",
                                  !["USD","GBP","EUR"].includes(currencyCode) && (isDark ? "bg-white/10 text-white" : "bg-white shadow-sm")
                                )}
                              >
                                <option value="">Other currency…</option>
                                {([
                                  ["NONE","No Currency"],
                                  ["AUD","Australian Dollar"],["CAD","Canadian Dollar"],["CHF","Swiss Franc"],
                                  ["JPY","Japanese Yen"],["SGD","Singapore Dollar"],["HKD","Hong Kong Dollar"],
                                  ["NZD","New Zealand Dollar"],["INR","Indian Rupee"],["AED","UAE Dirham"],
                                  ["SAR","Saudi Riyal"],["BRL","Brazilian Real"],["MXN","Mexican Peso"],
                                  ["ZAR","South African Rand"],["SEK","Swedish Krona"],["NOK","Norwegian Krone"],
                                  ["DKK","Danish Krone"],["PLN","Polish Złoty"],["CZK","Czech Koruna"],
                                  ["CNY","Chinese Yuan"],["KRW","South Korean Won"],["TWD","New Taiwan Dollar"],
                                  ["THB","Thai Baht"],["TRY","Turkish Lira"],["ILS","Israeli Shekel"],
                                ] as [string, string][]).map(([code, name]) => (
                                  <option key={code} value={code}>{getCurrencySymbol(code)} — {name}</option>
                                ))}
                              </select>
                              <div className={cn("pointer-events-none absolute right-3 top-1/2 -translate-y-1/2", isDark ? "text-white/30" : "text-black/30")}>
                                <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className={labelClasses}>Date Format</label>
                          <div className={cn("flex p-1 rounded-2xl border w-full max-w-[360px]", isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200")}>
                            {([
                              { key: 'auto', label: 'Auto' },
                              { key: 'us',   label: 'MM/DD/YYYY' },
                              { key: 'eu',   label: 'DD/MM/YYYY' },
                            ] as const).map(({ key, label }) => (
                              <button
                                key={key}
                                onClick={() => setDateFormat(key)}
                                className={cn(
                                  "flex-1 py-2 text-xs font-bold rounded-xl transition-all",
                                  dateFormat === key
                                    ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm")
                                    : "text-gray-400 hover:text-gray-500"
                                )}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <p className={cn("text-[10px] font-bold uppercase tracking-[0.15em] mt-2", isDark ? "text-white/20" : "text-[#1A1F36]/30")}>
                            Auto uses DD/MM for GBP/EUR, MM/DD otherwise
                          </p>
                        </div>

                        <div>
                          <label className={labelClasses}>Holiday Calendar</label>
                          <select
                            value={holidayCalendar}
                            onChange={(e) => setHolidayCalendar(e.target.value as HolidayCalendar)}
                            className={cn(
                              "w-full max-w-[320px] px-3 py-2 text-xs font-bold rounded-xl border transition-all appearance-none cursor-pointer",
                              isDark
                                ? "bg-white/5 border-white/10 text-white"
                                : "bg-white border-gray-200 text-black shadow-sm"
                            )}
                          >
                            {(Object.keys(CALENDAR_LABELS) as HolidayCalendar[]).map((cal) => (
                              <option key={cal} value={cal}>{CALENDAR_LABELS[cal]}</option>
                            ))}
                          </select>
                          <p className={cn("text-[10px] font-bold uppercase tracking-[0.15em] mt-2", isDark ? "text-white/20" : "text-[#1A1F36]/30")}>
                            Affects available day counts and NET30 business day calculation
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            </div>
          </motion.div>

          {/* Invoicing Presets Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={cn(cardClasses, "p-5 md:p-10")}
          >
            <div
              onClick={() => toggleSection('invoicing')}
              className={cn('flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none', collapsedSections['invoicing'] ? 'mb-0' : 'mb-8', isDark ? 'hover:bg-white/5' : 'hover:bg-black/5')}
            >
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                <Receipt size={20} />
              </div>
              <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Invoicing Presets</h2>
              <div className="ml-auto flex items-center gap-2">
                <span className={cn("text-[10px] font-bold uppercase tracking-widest opacity-40", isDark ? "text-white" : "text-[#1A1F36]")}>
                  {collapsedSections['invoicing'] ? 'Show' : 'Hide'}
                </span>
                <ChevronDown size={16} className={cn("transition-transform duration-200", isDark ? "text-white" : "text-[#1A1F36]")} style={{ transform: collapsedSections['invoicing'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              </div>
            </div>
            <AnimatePresence initial={false}>
            {!collapsedSections['invoicing'] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
            <div className="space-y-6">
              {/* Business Details subsection */}
              <div>
                <p className={sectionLabelClasses}>Business Details</p>
                <div className="space-y-4">
                  <div>
                    <label className={labelClasses}>Business Name</label>
                    <input
                      type="text"
                      className={inputClasses}
                      placeholder="Your business name"
                      value={businessName}
                      onChange={e => setBusinessName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Address</label>
                    <input
                      type="text"
                      className={inputClasses}
                      placeholder="Your business address"
                      value={businessAddress}
                      onChange={e => setBusinessAddress(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Registration / EIN #</label>
                    <input
                      type="text"
                      className={inputClasses}
                      placeholder="Tax ID, LLC number, etc."
                      value={regNumber}
                      onChange={e => setRegNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={cn("border-t", isDark ? "border-white/5" : "border-gray-100")} />

              {/* Payment Details subsection */}
              <div>
                <p className={sectionLabelClasses}>Payment Details</p>
                <div className="space-y-4">
                  <div>
                    <label className={labelClasses}>Bank Name</label>
                    <input
                      type="text"
                      className={inputClasses}
                      placeholder="e.g. Mercury, Chase"
                      value={bankName}
                      onChange={e => setBankName(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClasses}>Account #</label>
                      <input
                        type="text"
                        className={inputClasses}
                        placeholder="Account number"
                        value={accountNumber}
                        onChange={e => setAccountNumber(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Routing #</label>
                      <input
                        type="text"
                        className={inputClasses}
                        placeholder="Routing number"
                        value={routingNumber}
                        onChange={e => setRoutingNumber(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={cn("border-t", isDark ? "border-white/5" : "border-gray-100")} />

              {/* Invoice fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses}>Invoice Prefix</label>
                  <input
                    type="text"
                    className={inputClasses}
                    value={invoicePrefix}
                    onChange={e => setInvoicePrefix(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClasses}>Default Tax %</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={inputClasses}
                    value={defaultTaxRate}
                    onChange={e => setDefaultTaxRate(e.target.value.replace(/[^0-9.]/g, ''))}
                  />
                </div>
              </div>

              <div>
                <label className={labelClasses}>Default Payment Terms</label>
                <div className={cn("flex p-1 rounded-2xl border w-full max-w-[200px]", isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200")}>
                  {["NET30", "N/A"].map((term) => (
                    <button
                      key={term}
                      onClick={() => setPaymentTerms(term)}
                      className={cn(
                        "flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all",
                        paymentTerms === term
                          ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm")
                          : "text-gray-400"
                      )}
                    >{term}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelClasses}>Default Notes</label>
                <textarea
                  className={cn(inputClasses, "min-h-[120px] resize-none")}
                  placeholder="e.g. Payment due within 30 days. Thank you for your business."
                  value={defaultNotes}
                  onChange={e => setDefaultNotes(e.target.value)}
                />
              </div>
            </div>
            </motion.div>
            )}
            </AnimatePresence>
          </motion.div>

          <div className="rounded-[2.5rem] relative overflow-hidden" style={{ height: "240px" }}>
            <img
              src="https://res.cloudinary.com/dlmbskxuc/image/upload/v1772664226/fh_settingsmood_2_e1yku9.png"
              alt="Settings illustration"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          </div>

          {/* Tax Export Card */}
          <motion.div
            id="export-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className={cn(cardClasses, "p-5 md:p-10")}
          >
            <div
              onClick={() => toggleSection('export')}
              className={cn('flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none', collapsedSections['export'] ? 'mb-0' : 'mb-8', isDark ? 'hover:bg-white/5' : 'hover:bg-black/5')}
            >
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                <FileText size={20} />
              </div>
              <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Export Tax Summary</h2>
              <div className="ml-auto flex items-center gap-2">
                <span className={cn("text-[10px] font-bold uppercase tracking-widest opacity-40", isDark ? "text-white" : "text-[#1A1F36]")}>
                  {collapsedSections['export'] ? 'Show' : 'Hide'}
                </span>
                <ChevronDown size={16} className={cn("transition-transform duration-200", isDark ? "text-white" : "text-[#1A1F36]")} style={{ transform: collapsedSections['export'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              </div>
            </div>
            <AnimatePresence initial={false}>
            {!collapsedSections['export'] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
            <div className="space-y-6">
              {subscription.loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 rounded-full border-[3px] border-[#4A6CF7] border-t-transparent animate-spin" />
                </div>
              ) : !(subscription.status === 'active' && ['junior', 'lead'].includes(subscription.plan ?? '')) ? (
                /* Locked */
                <div className={cn("rounded-2xl border-2 border-dashed p-8 flex flex-col items-center gap-3 text-center", isDark ? "border-white/10" : "border-black/10")}>
                  <FileText size={20} className={isDark ? "text-white/20" : "text-[#1A1F36]/20"} />
                  <p className={cn("text-sm font-bold", isDark ? "text-white/40" : "text-[#1A1F36]/40")}>
                    Tax export is available on an active Junior or Lead subscription.
                  </p>
                  <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", isDark ? "text-white/20" : "text-[#1A1F36]/25")}>
                    Subscribe to generate your annual tax summary
                  </p>
                </div>
              ) : (
                <>
                  <p className={cn("text-sm", isDark ? "text-white/50" : "text-[#1A1F36]/50")}>
                    Generate a PDF summary of all your bookings for the year — ready to hand to your accountant.
                  </p>

                  {/* Year selector */}
                  <div>
                    <label className={labelClasses}>Tax Year</label>
                    <select
                      value={exportYear}
                      onChange={e => setExportYear(e.target.value)}
                      className={cn(inputClasses, "cursor-pointer")}
                    >
                      {exportYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>

                  {exportError && (
                    <p className="text-xs text-red-400 font-medium">{exportError}</p>
                  )}

                  <button
                    onClick={handleTaxExport}
                    disabled={exporting}
                    className={cn(
                      "w-full py-4 rounded-full flex items-center justify-center gap-3 font-bold text-xs uppercase tracking-[0.2em] transition-all",
                      exporting ? "opacity-50 cursor-not-allowed" : "hover:brightness-110 active:brightness-95",
                      isDark ? "bg-white text-[#1A1F36]" : "bg-[#1A1F36] text-white"
                    )}
                  >
                    {exporting ? <><Loader2 size={14} className="animate-spin" /> Generating PDF…</> : <><FileText size={14} /> Download Tax Summary</>}
                  </button>

                  <p className={cn("text-[10px] font-bold uppercase tracking-[0.15em] text-center", isDark ? "text-white/20" : "text-[#1A1F36]/30")}>
                    Includes all bookings, earnings, and days worked for {exportYear}.
                  </p>
                </>
              )}
            </div>
            </motion.div>
            )}
            </AnimatePresence>
          </motion.div>

          {/* Subscription Card — dynamic based on trial/active/expired state */}
          <motion.div
            id="subscription-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={cn(cardClasses, "p-5 md:p-10")}
          >
            <div
              onClick={() => toggleSection('subscription')}
              className={cn('flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none', collapsedSections['subscription'] ? 'mb-0' : 'mb-8', isDark ? 'hover:bg-white/5' : 'hover:bg-black/5')}
            >
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                <CreditCard size={20} />
              </div>
              <h2 className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1F36]")}>Subscription</h2>
              <div className="ml-auto flex items-center gap-2">
                <span className={cn("text-[10px] font-bold uppercase tracking-widest opacity-40", isDark ? "text-white" : "text-[#1A1F36]")}>
                  {collapsedSections['subscription'] ? 'Show' : 'Hide'}
                </span>
                <ChevronDown size={16} className={cn("transition-transform duration-200", isDark ? "text-white" : "text-[#1A1F36]")} style={{ transform: collapsedSections['subscription'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              </div>
            </div>
            <AnimatePresence initial={false}>
            {!collapsedSections['subscription'] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >

            {subscription.loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 rounded-full border-[3px] border-[#4A6CF7] border-t-transparent animate-spin" />
              </div>

            ) : subscription.isTrialing ? (
              /* ── STATE A: Actively trialing ─────────────────────────────── */
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-2">FREE TRIAL</div>
                <div className={cn("text-4xl font-black tracking-tight", isDark ? "text-white" : "text-[#1A1F36]")}>
                  {subscription.daysUntilTrialEnd} day{subscription.daysUntilTrialEnd === 1 ? '' : 's'} remaining
                </div>
                {subscription.trialEndsAt && (
                  <div className="mt-1 mb-8">
                    <p className={cn("text-[11px] font-bold uppercase tracking-[0.2em]", isDark ? "text-white/40" : "text-[#1A1F36]/40")}>
                      Trial ends {subscription.trialEndsAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}. Subscribe before it expires.
                    </p>
                    <p className={cn("text-[11px] font-bold uppercase tracking-[0.2em] mt-1", isDark ? "text-white/40" : "text-[#1A1F36]/40")}>
                      Subscribing now won't cut your trial short — you won't be charged until {subscription.trialEndsAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
                    </p>
                  </div>
                )}

                {/* Plan selection cards */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* Lead card */}
                  <button
                    onClick={() => setSelectedPlan('lead')}
                    className={`rounded-[2rem] p-6 text-left transition-all border-2 ${
                      selectedPlan === 'lead'
                        ? 'border-[#4A6CF7] bg-[#4A6CF7]/5'
                        : isDark ? 'border-white/10 bg-white/5 hover:border-white/20' : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'
                    }`}
                  >
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-2">LEAD PLAN</div>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className={cn("text-2xl font-black", isDark ? "text-white" : "text-[#1A1F36]")}>{pricing.sym}{pricing.lead}</span>
                      <span className="text-xs font-bold text-gray-400">/mo</span>
                    </div>
                    <ul className="space-y-1.5">
                      {['Everything in Junior', 'Estimated Taxes', 'Analytics', 'Invoices'].map(f => (
                        <li key={f} className={cn("flex items-center gap-2 text-[10px] font-bold", isDark ? "text-white/60" : "text-[#1A1F36]/60")}>
                          <Check size={10} className={selectedPlan === 'lead' ? 'text-[#4A6CF7] shrink-0' : isDark ? 'text-white/20 shrink-0' : 'text-gray-300 shrink-0'} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>

                  {/* Junior card */}
                  <button
                    onClick={() => setSelectedPlan('junior')}
                    className={`rounded-[2rem] p-6 text-left transition-all border-2 ${
                      selectedPlan === 'junior'
                        ? 'border-[#4A6CF7] bg-[#4A6CF7]/5'
                        : isDark ? 'border-white/10 bg-white/5 hover:border-white/20' : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'
                    }`}
                  >
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-2">JUNIOR PLAN</div>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className={cn("text-2xl font-black", isDark ? "text-white" : "text-[#1A1F36]")}>{pricing.sym}{pricing.junior}</span>
                      <span className="text-xs font-bold text-gray-400">/mo</span>
                    </div>
                    <ul className="space-y-1.5">
                      {['Booking Calendar', 'Hold Tracking', 'Income Dashboard'].map(f => (
                        <li key={f} className={cn("flex items-center gap-2 text-[10px] font-bold", isDark ? "text-white/60" : "text-[#1A1F36]/60")}>
                          <Check size={10} className={selectedPlan === 'junior' ? 'text-[#4A6CF7] shrink-0' : isDark ? 'text-white/20 shrink-0' : 'text-gray-300 shrink-0'} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                </div>

                <button
                  onClick={() => startCheckout(selectedPlan)}
                  disabled={checkoutLoading}
                  className={cn(
                    "w-full py-3 rounded-full font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:opacity-80 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed",
                    isDark ? "bg-white text-[#1A1F36]" : "bg-black text-white"
                  )}
                >
                  {checkoutLoading ? 'Redirecting...' : `Subscribe to ${selectedPlan === 'lead' ? 'Lead' : 'Junior'} — ${pricing.sym}${selectedPlan === 'lead' ? pricing.lead : pricing.junior}/mo`}
                </button>
                {checkoutError && (
                  <p className="text-xs text-red-400 font-bold text-center mt-3">{checkoutError}</p>
                )}
              </div>

            ) : subscription.status === 'active' ? (
              /* ── STATE B: Active paid subscriber ────────────────────────── */
              <div className="flex flex-col sm:flex-row sm:items-start gap-8">
                {/* Left — plan + price */}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-3">
                    {subscription.plan === 'lead' ? 'Lead Plan' : 'Junior Plan'}
                  </div>
                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span className={cn("text-4xl font-black tracking-tight", isDark ? "text-white" : "text-[#1A1F36]")}>
                      {subscription.plan === 'lead' ? `${pricing.sym}${pricing.lead}` : `${pricing.sym}${pricing.junior}`}
                    </span>
                    <span className={cn("text-sm font-bold", isDark ? "text-white/40" : "text-[#1A1F36]/40")}>/month</span>
                  </div>
                  <p className={cn("text-[11px] font-semibold", isDark ? "text-white/30" : "text-[#1A1F36]/30")}>
                    Billed monthly · Cancel anytime
                  </p>
                </div>

                {/* Right — features + action */}
                <div className="flex-1 min-w-0 flex flex-col gap-5">
                  <ul className="space-y-2">
                    {(subscription.plan === 'lead'
                      ? ['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management']
                      : ['Booking Calendar', 'Hold Tracking', 'Income Dashboard']
                    ).map(f => (
                      <li key={f} className={cn("flex items-center gap-2 text-xs font-bold", isDark ? "text-white/60" : "text-[#1A1F36]/60")}>
                        <Check size={12} className="text-[#4A6CF7] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {subscription.plan === 'junior' ? (
                    <button
                      onClick={() => startCheckout('lead')}
                      disabled={checkoutLoading}
                      className={cn(
                        "w-full px-6 py-2.5 rounded-full font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:opacity-80 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed",
                        isDark ? "bg-white text-[#1A1F36]" : "bg-[#1A1F36] text-white"
                      )}
                    >
                      {checkoutLoading ? 'Redirecting...' : `Upgrade to Lead — ${pricing.sym}${pricing.lead}/mo`}
                    </button>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-1.5 text-[#4A6CF7]">
                        <CheckCircle size={13} />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">You're on the best plan</span>
                      </div>
                      <button
                        onClick={() => openPortal()}
                        disabled={checkoutLoading}
                        className={cn(
                          "w-full px-6 py-2.5 rounded-full font-black uppercase tracking-[0.15em] text-[10px] border transition-all hover:opacity-70 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed",
                          isDark ? "border-white/20 text-white/60 hover:border-white/40" : "border-[#1A1F36]/20 text-[#1A1F36]/60 hover:border-[#1A1F36]/40"
                        )}
                      >
                        {checkoutLoading ? 'Redirecting...' : 'Manage billing & plan'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

            ) : (
              /* ── STATE C: Expired / past_due fallback ───────────────────── */
              /* TrialExpiredGate should block before reaching here, but just in case */
              <div className="text-center py-4">
                <p className={cn("text-sm font-bold mb-6", isDark ? "text-white/50" : "text-[#1A1F36]/50")}>
                  Subscription required.
                </p>
                <button
                  onClick={() => startCheckout('lead')}
                  disabled={checkoutLoading}
                  className={cn(
                    "w-full py-3 rounded-full font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed",
                    isDark ? "bg-white text-[#1A1F36]" : "bg-black text-white"
                  )}
                >
                  {checkoutLoading ? 'Redirecting...' : 'Subscribe to continue'}
                </button>
                {checkoutError && (
                  <p className="text-xs text-red-400 font-bold text-center mt-3">{checkoutError}</p>
                )}
              </div>
            )}
            </motion.div>
            )}
            </AnimatePresence>
          </motion.div>

          {/* Log Out */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: prefersReducedMotion ? 0 : 0.2 }}
            className={cn(
              "backdrop-blur-2xl rounded-[2.5rem] border shadow-xl p-5 md:p-10",
              isDark ? "bg-white/[0.03] border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#1a1f2e]" : "bg-white/90 border-black/5 [@supports_not_(backdrop-filter:blur(1px))]:bg-white"
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className={`text-[14px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/40" : "text-slate-500"}`}>Account</p>
                <p className={`text-[13px] mt-1 ${isDark ? "text-white/30" : "text-slate-400"}`}>{email}</p>

                {!showEmailChange ? (
                  <button
                    onClick={() => { setShowEmailChange(true); setEmailChangeMsg(null); setNewEmail(""); }}
                    className={`mt-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${isDark ? "text-white/20 hover:text-white/50" : "text-[#1A1F36]/25 hover:text-[#1A1F36]/50"}`}
                  >
                    Change email
                  </button>
                ) : (
                  <div className="mt-4 space-y-2 max-w-sm">
                    <input
                      type="email"
                      placeholder="New email address"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      className={cn(
                        "w-full px-4 py-2.5 rounded-2xl border text-sm font-medium outline-none transition-all",
                        isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-[#4A6CF7]" : "bg-black/5 border-black/10 text-[#1A1F36] placeholder-black/20 focus:border-[#4A6CF7]"
                      )}
                    />
                    {emailChangeMsg && (
                      <p className={`text-[11px] font-bold ${emailChangeMsg.type === 'success' ? 'text-emerald-500' : 'text-red-400'}`}>
                        {emailChangeMsg.text}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        disabled={emailChanging || !newEmail || newEmail === email}
                        onClick={async () => {
                          setEmailChanging(true);
                          setEmailChangeMsg(null);
                          const { error } = await supabase.auth.updateUser({ email: newEmail });
                          if (error) {
                            setEmailChangeMsg({ type: 'error', text: error.message });
                          } else {
                            setEmailChangeMsg({ type: 'success', text: 'Confirmation sent to your new address. Check your inbox.' });
                            setShowEmailChange(false);
                            setNewEmail("");
                          }
                          setEmailChanging(false);
                        }}
                        className={cn(
                          "px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] transition-all disabled:opacity-40 disabled:cursor-not-allowed",
                          isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-[#1A1F36] text-white hover:opacity-80"
                        )}
                      >
                        {emailChanging ? 'Sending…' : 'Send confirmation'}
                      </button>
                      <button
                        onClick={() => { setShowEmailChange(false); setNewEmail(""); setEmailChangeMsg(null); }}
                        className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] transition-all ${isDark ? "text-white/30 hover:text-white/60" : "text-[#1A1F36]/30 hover:text-[#1A1F36]/60"}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={async () => { await supabase.auth.signOut(); window.location.href = '/login'; }}
                className={cn(
                  "px-6 py-3 rounded-2xl text-[13px] font-bold transition-all shrink-0",
                  isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-black/5 text-[#1A1F36] hover:bg-black/10"
                )}
              >
                Log Out
              </button>
            </div>
          </motion.div>

          {/* GDPR Account Deletion — EU users only */}
          {region === 'EU' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: prefersReducedMotion ? 0 : 0.25 }}
              className={cn(
                "backdrop-blur-2xl rounded-[2.5rem] border shadow-xl p-5 md:p-10",
                isDark ? "bg-red-950/20 border-red-900/30 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#2a1414]" : "bg-red-50/80 border-red-200 [@supports_not_(backdrop-filter:blur(1px))]:bg-red-50"
              )}
            >
              <div
                onClick={() => toggleSection('deletion')}
                className={`flex items-center gap-3 cursor-pointer rounded-2xl px-3 -mx-3 transition-colors select-none hover:bg-red-500/10 ${collapsedSections['deletion'] ? 'mb-0' : 'mb-6'}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/20 text-red-400">
                  <AlertTriangle size={20} />
                </div>
                <h2 className="text-xl font-bold text-red-400">Account Deletion</h2>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-40 text-red-400">
                    {collapsedSections['deletion'] ? 'Show' : 'Hide'}
                  </span>
                  <ChevronDown size={16} className="text-red-400 transition-transform duration-200" style={{ transform: collapsedSections['deletion'] ? 'rotate(0deg)' : 'rotate(180deg)' }} />
                </div>
              </div>
              <AnimatePresence initial={false}>
              {!collapsedSections['deletion'] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <p className={cn("text-sm font-light leading-relaxed mb-8", isDark ? "text-white/50" : "text-[#1A1F36]/60")}>
                  Under GDPR you have the right to request deletion of all your personal data. This action is <strong>permanent and irreversible</strong> — all bookings, invoices, settings, and files will be deleted immediately.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className={labelClasses}>Your Password</label>
                    <input
                      type="password"
                      className={cn(inputClasses, "border-red-500/30 focus:border-red-400")}
                      placeholder="Enter your current password"
                      value={deletePassword}
                      onChange={e => setDeletePassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Type DELETE to confirm</label>
                    <input
                      type="text"
                      className={cn(inputClasses, "border-red-500/30 focus:border-red-400")}
                      placeholder="DELETE"
                      value={deleteConfirm}
                      onChange={e => setDeleteConfirm(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirm !== 'DELETE' || !deletePassword || deleting}
                    className={cn(
                      "w-full py-4 rounded-full font-bold text-xs uppercase tracking-[0.2em] transition-all border-2 border-red-500",
                      deleteConfirm === 'DELETE' && deletePassword && !deleting
                        ? "bg-red-500 text-white hover:bg-red-600 active:scale-95"
                        : "bg-transparent text-red-400/30 border-red-500/30 cursor-not-allowed"
                    )}
                  >
                    {deleting ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Deleting…</span> : 'Delete My Account'}
                  </button>
                </div>
              </motion.div>
              )}
              </AnimatePresence>
            </motion.div>
          )}

        </div>

        <AnimatePresence>
          {showPasswordModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-8 backdrop-blur-xl bg-black/40 overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/80">
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className={`${cardClasses} w-full max-w-[95vw] sm:max-w-md p-4 sm:p-6 md:p-10 relative`}>
                <button onClick={() => setShowPasswordModal(false)} className="absolute top-8 right-8 p-2 rounded-full hover:bg-white/10 transition-colors"><X size={20} /></button>
                <h2 className={`text-2xl font-black mb-8 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Change Password</h2>
                <div className="space-y-4">
                  <div><label className={labelClasses}>Current Password</label><input type="password" className={inputClasses} placeholder="••••••••" /></div>
                  <div><label className={labelClasses}>New Password</label><input type="password" className={inputClasses} placeholder="••••••••" /></div>
                  <div><label className={labelClasses}>Confirm New Password</label><input type="password" className={inputClasses} placeholder="••••••••" /></div>
                </div>
                <button className="w-full mt-8 bg-black text-white py-4 rounded-full font-bold uppercase tracking-[0.2em]">Update Password</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </main>

    </div>
  );
}
